import {
    Checkbox,
    DefaultButton,
    Label,
    Panel,
    PanelType,
    PrimaryButton,
    Separator,
    Stack,
    Text,
    TextField
} from "@fluentui/react";
import { RootState } from "app/store";
import CESDropdown from "components/controls/CESDropdown";
import { createGroupNumber, fadeIn, panelButtonsStyles } from "global/Application";
import { addUpdateGroupStructure } from "global/Reducer/dataSlice";
import {
    ICESNumber as FAM_ICESNumber,
    IDepartment as FAM_IDepartment,
    IGroupStructure as FAM_IGroupStructure
} from "model/FlexibleAttributeModel";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from 'uuid';
import { EView } from "../EView.type";
import { IGroupStructureDepartmentWizardProps } from "./IGroupStructureDepartmentWizardProps.type";


const GroupStructureDepartmentWizard: React.FunctionComponent<IGroupStructureDepartmentWizardProps> = (props) => {


    const state = useSelector((state: RootState) => state);

    const dispatch = useDispatch();
    const [cesSelected, setCesSelected] = useState<FAM_ICESNumber | null>(null);
    const [newDepartments, setNewDepartments] = useState<FAM_IDepartment[] | null>(null);

    // New-CES/Department
    const [multiLine, setMultiLine] = useState<boolean>(false);


    useEffect(() => {
        if (cesSelected) {
            // group number for use if we are adding group number
            // -------------------------------------------------
            const groupNumber = createGroupNumber(state.data);
            groupNumber.cesNumberRef = { id: cesSelected.id, name: cesSelected.cesNumber };

            // department for use if we are adding department
            // -------------------------------------------------
            const depart: FAM_IDepartment[] = [];
            depart.push({
                id: uuidv4(),
                cesNumberRef: { id: cesSelected.id, name: cesSelected.cesNumber },
                description: '',
                name: '',
                deactivated: false,
                fields: [],
            });
            setNewDepartments(depart);
        }
        else {
            setNewDepartments(null);
        }
    }, [cesSelected]);


    function onRenderBody() {
        return (
            <Stack tokens={{ childrenGap: '15px' }}>
                <Separator />
                <CESDropdown
                    value={cesSelected ?? undefined}
                    onChange={(option) => {
                        if (option.cesNumber)
                            setCesSelected({
                                id: option.id,
                                cesNumber: option.cesNumber,
                                deactivated: false,
                            });
                    }}
                />
                {cesSelected && newDepartments &&
                    <>
                        <Label>Associate Department</Label>
                        {multiLine &&
                            <TextField
                                autoComplete={'off'}
                                multiline={true}
                                rows={5}
                                onChange={(event, newValue) => {
                                    //TODO clean string from bad characters
                                    //TODO add a denouncer
                                    const departments = newValue?.split('\n').map(eachLine => {
                                        const [name, description] = eachLine.split(',');
                                        const dep: FAM_IDepartment = {
                                            cesNumberRef: {
                                                id: cesSelected.id,
                                                name: cesSelected.cesNumber,
                                            },
                                            id: uuidv4(),
                                            name: name,
                                            description: description,
                                            deactivated: false,
                                            fields: [],
                                        }

                                        return dep;
                                    });

                                    if (departments && departments.length)
                                        setNewDepartments(departments);
                                }}
                                defaultValue={newDepartments?.filter(q => q.name !== '').map(q => `${q.name},${q.description ?? ""}`)?.join('\n')}
                            />
                        }

                        {!multiLine && newDepartments?.map((q, index) => {
                            return (
                                <Stack key={q.id} horizontal verticalFill tokens={{ childrenGap: '10px' }}>
                                    <Stack.Item grow={1} >
                                        <TextField
                                            id={`groupStructure-department-name-${index}`}
                                            autoComplete={'off'}
                                            label={`${index + 1} - Department ID`}
                                            onChange={(event, newValue) => {
                                                if (newValue !== undefined) {
                                                    const t = [...newDepartments];
                                                    t[index].name = newValue;

                                                    // is the one after this not defined?
                                                    if (t[index + 1] === undefined) {
                                                        t.push({
                                                            id: uuidv4(),
                                                            cesNumberRef: {
                                                                id: cesSelected.id,
                                                                name: cesSelected.cesNumber
                                                            },
                                                            name: '',
                                                            description: '',
                                                            deactivated: false,
                                                            fields: [],
                                                        })
                                                    }

                                                    // is the current one empty, remove unless its first one in the list
                                                    if (index !== 0 && t[index].name === '')
                                                        t.splice(index, 1);

                                                    setNewDepartments(t);
                                                }
                                            }}
                                            onKeyDown={(event) => {
                                                if (event.code === 'Enter' || event.code === "NumpadEnter")
                                                    document.getElementById(`groupStructure-department-name-${index + 1}`)?.focus();

                                                if (event.code === 'Backspace' && newDepartments[index].name === '')
                                                    document.getElementById(`groupStructure-department-name-${index - 1}`)?.focus();
                                            }}
                                            value={q.name ?? ""}
                                        />
                                    </Stack.Item>
                                    <Stack.Item grow={12} >
                                        <TextField
                                            id={`groupStructure-department-name-${index}`}
                                            autoComplete={'off'}
                                            label={`Description`}
                                            rows={2}
                                            onChange={(event, newValue) => {
                                                if (newValue !== undefined) {
                                                    const t = [...newDepartments];
                                                    t[index].description = newValue;

                                                    setNewDepartments(t);
                                                }
                                            }}
                                            onKeyDown={(event) => {
                                                if (event.code === 'Enter' || event.code === "NumpadEnter")
                                                    document.getElementById(`groupStructure-department-name-${index + 1}`)?.focus();

                                                if (event.code === 'Backspace' && newDepartments[index].name === '')
                                                    document.getElementById(`groupStructure-department-name-${index - 1}`)?.focus();
                                            }}
                                            value={q.description ?? ""}
                                        />
                                    </Stack.Item>
                                </Stack>
                            )
                        })}
                        <div>
                            <Checkbox
                                label="Use multiline input instead."
                                checked={multiLine}
                                onChange={() => { setMultiLine(!multiLine) }}
                            />
                            <Text variant="smallPlus">Each new line will be translated to a department number: 1XX,department description</Text>
                        </div>
                    </>
                }
            </Stack>
        );
    }

    function onRenderFooter() {
        return <div className={fadeIn}>
            <PrimaryButton text='Continue'
                styles={panelButtonsStyles}
                onClick={() => {
                    const dispatchDepartments = [...state.data.json.groupStructure.departments];

                    let departmentChanged = false;

                    // associating Departments?
                    newDepartments?.forEach(q => {
                        if (q.name) {
                            dispatchDepartments.push(q);
                            departmentChanged = true;
                        }
                    })

                    const patch: Partial<FAM_IGroupStructure> = {}

                    if (departmentChanged)
                        patch.departments = dispatchDepartments;

                    dispatch(addUpdateGroupStructure(patch));

                    setCesSelected(null);
                    props.setView(EView.entry);
                    props.onDismiss?.();
                }}
            />
            <DefaultButton text='Cancel'
                onClick={() => { props.setView(EView.entry); }}
                styles={panelButtonsStyles}
            />
        </div>;
    }

    return <Panel
        isOpen={props.isOpen}
        onDismiss={props.onCancel}
        type={PanelType.medium}
        layerProps={{ eventBubblingEnabled: true }}
        headerText="Manage | Group Structure"
        onRenderFooterContent={onRenderFooter}
        isFooterAtBottom={true}
    >
        {onRenderBody()}
    </Panel>
}

export default GroupStructureDepartmentWizard;